import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CandidateDashboardLayout from '../components/layouts/CandidateDashboardLayout';
import Icon from '../components/atoms/Icon';
import Badge from '../components/atoms/Badge';
import SessionManager from '../utils/sessionManager';
import { Chart as ChartJS, ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import { API_BASE_URL, FALLBACK_API_URL } from '../config';

// Register ChartJS components
ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const CandidatePlacementTrainingDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [assessments, setAssessments] = useState([]);

  const session = SessionManager.getCurrentSession();
  const apiUrl = API_BASE_URL || FALLBACK_API_URL;

  useEffect(() => {
    if (!session) {
      navigate('/login');
      return;
    }
    loadAssessments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAssessments = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${apiUrl}/placement-training/assessments/candidate/history`,
        {
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAssessments(data.assessments || []);
      }
    } catch (error) {
      console.error('Error loading assessments:', error);
    } finally {
      setLoading(false);
    }
  };

  const getGrade = (score) => {
    if (!score) return { grade: 'N/A', color: 'text-gray-400' };
    if (score >= 90) return { grade: 'A+', color: 'text-green-600' };
    if (score >= 80) return { grade: 'A', color: 'text-green-600' };
    if (score >= 70) return { grade: 'B+', color: 'text-blue-600' };
    if (score >= 60) return { grade: 'B', color: 'text-blue-600' };
    if (score >= 50) return { grade: 'C', color: 'text-yellow-600' };
    return { grade: 'F', color: 'text-red-600' };
  };

  if (loading) {
    return (
      <CandidateDashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
        </div>
      </CandidateDashboardLayout>
    );
  }

  const completedAssessments = assessments.filter(a => a.status === 'completed');
  const passedAssessments = assessments.filter(a => a.passed);
  const inProgressAssessments = assessments.filter(a => a.status === 'in_progress');

  // Chart data for assessment status
  const statusChartData = {
    labels: ['Passed', 'Not Passed', 'In Progress'],
    datasets: [{
      data: [
        passedAssessments.length,
        completedAssessments.length - passedAssessments.length,
        inProgressAssessments.length
      ],
      backgroundColor: ['#10b981', '#ef4444', '#3b82f6'],
      borderWidth: 0,
    }]
  };

  // Chart data for scores
  const recentCompleted = completedAssessments.slice(0, 5).reverse();
  const scoresChartData = {
    labels: recentCompleted.map(a => a.position_title.substring(0, 15) + '...'),
    datasets: [{
      label: 'Score %',
      data: recentCompleted.map(a => a.overall_score || 0),
      backgroundColor: recentCompleted.map(a =>
        a.overall_score >= 70 ? '#10b981' :
        a.overall_score >= 50 ? '#f59e0b' : '#ef4444'
      ),
      borderRadius: 6,
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: { size: 14, weight: 'bold' },
        bodyFont: { size: 13 },
        cornerRadius: 8,
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        ticks: { callback: (value) => value + '%' }
      }
    }
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: { padding: 15, font: { size: 12 } }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        cornerRadius: 8,
      }
    },
    cutout: '70%',
  };

  return (
    <CandidateDashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Placement Training Progress</h1>
          <p className="text-gray-600 mt-1">MNC assessment performance and analytics</p>
        </div>

        {assessments.length === 0 ? (
          /* Empty State */
          <div className="bg-white rounded-xl shadow-sm p-12 text-center border border-gray-200">
            <Icon name="school" size={64} className="text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">No Assessments Yet</h3>
            <p className="text-gray-600 mb-6">
              Start your placement training journey by taking an assessment
            </p>
            <button
              onClick={() => navigate('/placement-training')}
              className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-semibold inline-flex items-center gap-2"
            >
              <Icon name="explore" size={20} />
              Browse Companies
            </button>
          </div>
        ) : (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                    <Icon name="assessment" size={24} className="text-white" />
                  </div>
                  <span className="text-3xl font-bold text-blue-900">{assessments.length}</span>
                </div>
                <p className="text-sm font-semibold text-blue-700">Total Assessments</p>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border border-green-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center">
                    <Icon name="check_circle" size={24} className="text-white" />
                  </div>
                  <span className="text-3xl font-bold text-green-900">{passedAssessments.length}</span>
                </div>
                <p className="text-sm font-semibold text-green-700">Passed</p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 border border-purple-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center">
                    <Icon name="workspace_premium" size={24} className="text-white" />
                  </div>
                  <span className="text-3xl font-bold text-purple-900">
                    {assessments.filter(a => a.certificate_issued).length}
                  </span>
                </div>
                <p className="text-sm font-semibold text-purple-700">Certificates</p>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6 border border-orange-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-12 h-12 bg-orange-600 rounded-lg flex items-center justify-center">
                    <Icon name="pending" size={24} className="text-white" />
                  </div>
                  <span className="text-3xl font-bold text-orange-900">{inProgressAssessments.length}</span>
                </div>
                <p className="text-sm font-semibold text-orange-700">In Progress</p>
              </div>
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Status Distribution Chart */}
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                <h3 className="text-base font-semibold text-gray-900 mb-4">Assessment Status</h3>
                <div className="h-64 flex items-center justify-center">
                  <Doughnut data={statusChartData} options={doughnutOptions} />
                </div>
              </div>

              {/* Recent Scores Chart */}
              {recentCompleted.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                  <h3 className="text-base font-semibold text-gray-900 mb-4">Recent Assessment Scores</h3>
                  <div className="h-64">
                    <Bar data={scoresChartData} options={chartOptions} />
                  </div>
                </div>
              )}
            </div>

            {/* Recent Assessments List */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-gray-900">Recent Assessments</h3>
                <button
                  onClick={() => navigate('/placement-training')}
                  className="text-sm text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1"
                >
                  <span>Browse Companies</span>
                  <Icon name="arrow_forward" size={16} />
                </button>
              </div>
              <div className="space-y-3">
                {assessments.slice(0, 5).map((assessment) => {
                  const grade = getGrade(assessment.overall_score);
                  return (
                    <div
                      key={assessment.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer border border-gray-200"
                      onClick={() => navigate(`/placement-training/assessment/${assessment.id}/results`)}
                    >
                      <div className="flex items-center gap-4 flex-1">
                        <div className="w-12 h-12 rounded-lg bg-white border border-gray-200 flex items-center justify-center">
                          <Icon name="business" size={24} className="text-gray-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-900 truncate">{assessment.position_title}</p>
                          <p className="text-sm text-gray-600">{assessment.company_name}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        {assessment.status === 'completed' ? (
                          <>
                            <div className="text-right">
                              <div className={`text-2xl font-bold ${
                                assessment.overall_score >= 70 ? 'text-green-600' :
                                assessment.overall_score >= 50 ? 'text-yellow-600' : 'text-red-600'
                              }`}>
                                {assessment.overall_score?.toFixed(0)}%
                              </div>
                              <div className={`text-xs font-semibold ${grade.color}`}>
                                Grade {grade.grade}
                              </div>
                            </div>
                            {assessment.passed && (
                              <Icon name="verified" size={24} className="text-green-600" />
                            )}
                          </>
                        ) : (
                          <Badge variant="info">In Progress</Badge>
                        )}
                        <Icon name="chevron_right" size={24} className="text-gray-400" />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
    </CandidateDashboardLayout>
  );
};

export default CandidatePlacementTrainingDashboard;
